import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  Search, 
  X, 
  Home, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  MapPin,
  Users,
  Euro,
  Loader2
} from 'lucide-react';
import { format, parseISO, isBefore, isAfter, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Composant de recherche de logements avec historique des prestations.
 * Affiche toutes les prestations passées et futures pour un logement sélectionné.
 */
const PropertyHistorySearch = ({ onNavigateToTask, className = '' }) => {
  const { token } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyTasks, setPropertyTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const searchRef = useRef(null);

  // Charger les propriétés au montage
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await fetch(`${API_URL}/api/properties`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setProperties(data);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };
    
    if (token) {
      fetchProperties();
    }
  }, [token]);

  // Filtrer les propriétés en fonction de la recherche
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProperties([]);
      setShowDropdown(false);
      return;
    }

    const search = searchQuery.toLowerCase();
    const filtered = properties.filter(p => 
      p.name?.toLowerCase().includes(search) ||
      p.address?.toLowerCase().includes(search) ||
      p.city?.toLowerCase().includes(search)
    ).slice(0, 10); // Limiter à 10 résultats

    setFilteredProperties(filtered);
    setShowDropdown(filtered.length > 0);
  }, [searchQuery, properties]);

  // Charger les tâches d'un logement sélectionné
  const loadPropertyTasks = useCallback(async (propertyId) => {
    setLoadingTasks(true);
    try {
      // Charger toutes les tâches pour ce logement (passées et futures)
      const response = await fetch(`${API_URL}/api/tasks?property_id=${propertyId}&limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Trier par date (les plus récentes en premier pour les terminées, les prochaines d'abord pour les futures)
        const sorted = data.sort((a, b) => {
          const dateA = parseISO(a.date);
          const dateB = parseISO(b.date);
          return dateB - dateA; // Plus récent en premier
        });
        setPropertyTasks(sorted);
      }
    } catch (error) {
      console.error('Error fetching property tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  }, [token]);

  // Sélectionner un logement
  const handleSelectProperty = (property) => {
    setSelectedProperty(property);
    setSearchQuery(property.name);
    setShowDropdown(false);
    setExpanded(true);
    loadPropertyTasks(property.id);
  };

  // Réinitialiser la recherche
  const handleClear = () => {
    setSearchQuery('');
    setSelectedProperty(null);
    setPropertyTasks([]);
    setExpanded(false);
    setShowDropdown(false);
  };

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Obtenir le badge de statut
  const getStatusBadge = (status) => {
    const styles = {
      'planifie': 'bg-blue-100 text-blue-700 border-blue-200',
      'en_cours': 'bg-amber-100 text-amber-700 border-amber-200',
      'termine': 'bg-emerald-100 text-emerald-700 border-emerald-200',
      'valide': 'bg-green-100 text-green-700 border-green-200',
      'annule': 'bg-red-100 text-red-700 border-red-200'
    };
    const labels = {
      'planifie': 'Planifié',
      'en_cours': 'En cours',
      'termine': 'Terminé',
      'valide': 'Validé',
      'annule': 'Annulé'
    };
    return (
      <Badge className={`${styles[status] || 'bg-gray-100'} border text-xs`}>
        {labels[status] || status}
      </Badge>
    );
  };

  // Séparer les tâches en passées et futures
  const now = new Date();
  const futureTasks = propertyTasks.filter(t => {
    const taskDate = parseISO(t.date);
    return isAfter(taskDate, now) || isToday(taskDate);
  }).sort((a, b) => parseISO(a.date) - parseISO(b.date)); // Prochaines en premier

  const pastTasks = propertyTasks.filter(t => {
    const taskDate = parseISO(t.date);
    return isBefore(taskDate, now) && !isToday(taskDate);
  }).sort((a, b) => parseISO(b.date) - parseISO(a.date)); // Plus récentes en premier

  return (
    <div className={`${className}`} ref={searchRef}>
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
              <Search className="h-4 w-4 text-blue-600" />
              Rechercher un logement
            </CardTitle>
            {selectedProperty && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="text-slate-500 hover:text-slate-700"
              >
                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {/* Barre de recherche */}
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Tapez le nom d'un logement..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9 h-10 rounded-xl border-slate-200 focus:border-blue-300 focus:ring-blue-200"
                data-testid="property-history-search-input"
              />
              {searchQuery && (
                <button
                  onClick={handleClear}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Dropdown des résultats */}
            {showDropdown && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                {filteredProperties.map((property) => (
                  <button
                    key={property.id}
                    onClick={() => handleSelectProperty(property)}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-start gap-3 border-b border-slate-100 last:border-b-0"
                    data-testid={`property-option-${property.id}`}
                  >
                    <Home className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate">{property.name}</p>
                      {property.address && (
                        <p className="text-sm text-slate-500 truncate flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {property.address}, {property.city}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Résultat sélectionné */}
          {selectedProperty && expanded && (
            <div className="mt-4">
              {/* Info du logement */}
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Home className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800">{selectedProperty.name}</p>
                  <p className="text-sm text-slate-500 truncate">
                    {selectedProperty.address}, {selectedProperty.city}
                  </p>
                </div>
                <Badge className="bg-slate-100 text-slate-600 border-0">
                  {propertyTasks.length} prestation{propertyTasks.length > 1 ? 's' : ''}
                </Badge>
              </div>

              {loadingTasks ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : propertyTasks.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  <p>Aucune prestation pour ce logement</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Prestations à venir */}
                  {futureTasks.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        À venir ({futureTasks.length})
                      </h4>
                      <div className="space-y-2">
                        {futureTasks.map((task) => (
                          <button
                            key={task.id}
                            onClick={() => onNavigateToTask && onNavigateToTask(task)}
                            className="w-full p-3 bg-emerald-50 hover:bg-emerald-100 rounded-xl border border-emerald-200 text-left transition-colors"
                            data-testid={`task-future-${task.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-emerald-600" />
                                <span className="font-medium text-slate-800">
                                  {format(parseISO(task.date), 'EEEE d MMMM yyyy', { locale: fr })}
                                </span>
                              </div>
                              {getStatusBadge(task.status)}
                            </div>
                            {task.housekeeper_names && task.housekeeper_names.length > 0 && (
                              <div className="mt-1 flex items-center gap-1 text-sm text-slate-600">
                                <Users className="h-3 w-3" />
                                <span>{task.housekeeper_names.join(', ')}</span>
                              </div>
                            )}
                            {task.price > 0 && (
                              <div className="mt-1 flex items-center gap-1 text-sm text-slate-600">
                                <Euro className="h-3 w-3" />
                                <span>{task.price}€</span>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Prestations passées */}
                  {pastTasks.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 mb-2 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" />
                        Historique ({pastTasks.length})
                      </h4>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {pastTasks.map((task) => (
                          <button
                            key={task.id}
                            onClick={() => onNavigateToTask && onNavigateToTask(task)}
                            className="w-full p-3 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 text-left transition-colors"
                            data-testid={`task-past-${task.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-slate-400" />
                                <span className="font-medium text-slate-700">
                                  {format(parseISO(task.date), 'EEEE d MMMM yyyy', { locale: fr })}
                                </span>
                              </div>
                              {getStatusBadge(task.status)}
                            </div>
                            {task.housekeeper_names && task.housekeeper_names.length > 0 && (
                              <div className="mt-1 flex items-center gap-1 text-sm text-slate-500">
                                <Users className="h-3 w-3" />
                                <span>{task.housekeeper_names.join(', ')}</span>
                              </div>
                            )}
                            {task.price > 0 && (
                              <div className="mt-1 flex items-center gap-1 text-sm text-slate-500">
                                <Euro className="h-3 w-3" />
                                <span>{task.price}€</span>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PropertyHistorySearch;
